// src/pages/analytics/CustomReportBuilder.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Tabs,
  Tab,
  Breadcrumbs,
  Link,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Stack,
  TextField,
  Chip,
  Divider,
  useTheme,
  alpha,
  Paper,
  IconButton,
  Autocomplete,
  CircularProgress,
  Radio,
  RadioGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  BarChart as BarChartIcon,
  ShowChart as LineChartIcon, // Fixed: LineChart is not available, using ShowChart instead
  PieChart as PieChartIcon,
  TableChart as TableChartIcon,
  FileDownload as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  NavigateNext as NavigateNextIcon,
  Launch as PreviewIcon,
  Assignment as TemplateIcon,
  Settings as SettingsIcon,
  Timeline as TimelineIcon,
  DateRange as DateRangeIcon,
  Tune as FilterIcon,
  ViewColumn as ColumnIcon,
  Public as PlatformIcon,
  Campaign as CampaignIcon,
  Article as ContentIcon,
  Business as BrandIcon,
  People as ClientIcon,
} from '@mui/icons-material';

// Import custom components
import MagicCard from '../../components/ui/MagicCard';
import { Button } from '../../design-system/components/core';
import MagicLoading from '../../components/ui/MagicLoading';
import ChartCard from '../../components/features/analytics/ChartCard';
import DateRangePicker from '../../components/ui/DateRangePicker';

// Import stores
import useAnalyticsStore from '../../stores/analyticsStore';
import useNotificationStore from '../../stores/notificationStore';

// Import utils
import { formatNumber } from '../../utils/analyticsUtils';

/**
 * Custom Report Builder Component
 * Allows users to create custom analytics reports
 */
const CustomReportBuilder = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Get services from stores
  const { 
    customReportData, 
    isLoading, 
    error,
    dateRange,
    setDateRange,
    fetchCustomReport,
    exportAnalyticsData,
  } = useAnalyticsStore();
  
  const { showError, showSuccess } = useNotificationStore();
  
  // Stepper state
  const [activeStep, setActiveStep] = useState(0);
  
  // Report configuration state
  const [reportConfig, setReportConfig] = useState({
    name: '',
    description: '',
    dataSource: 'all', // all, campaigns, content, social, clients, brands
    metrics: [],
    dimensions: [],
    dateRange: { 
      startDate: dateRange.startDate, 
      endDate: dateRange.endDate
    },
    filters: [],
    visualizations: [],
    schedule: null, // null, daily, weekly, monthly
    recipients: [],
    format: 'dashboard', // dashboard, excel, pdf
  });
  
  // Available metrics based on data source
  const getAvailableMetrics = () => {
    const baseMetrics = [
      { id: 'impressions', name: 'Impressions', group: 'Reach' },
      { id: 'reach', name: 'Reach', group: 'Reach' },
      { id: 'engagements', name: 'Engagements', group: 'Engagement' },
      { id: 'engagement_rate', name: 'Engagement Rate', group: 'Engagement' },
      { id: 'clicks', name: 'Clicks', group: 'Engagement' },
      { id: 'likes', name: 'Likes', group: 'Engagement' },
      { id: 'comments', name: 'Comments', group: 'Engagement' },
      { id: 'shares', name: 'Shares', group: 'Engagement' },
      { id: 'saves', name: 'Saves', group: 'Engagement' },
    ];
    
    // Add data source specific metrics
    switch (reportConfig.dataSource) {
      case 'campaigns':
        return [
          ...baseMetrics,
          { id: 'budget_spent', name: 'Budget Spent', group: 'Financial' },
          { id: 'budget_total', name: 'Budget Total', group: 'Financial' },
          { id: 'budget_percent', name: 'Budget %', group: 'Financial' },
          { id: 'conversion_rate', name: 'Conversion Rate', group: 'Performance' },
          { id: 'cpc', name: 'Cost per Click', group: 'Performance' },
          { id: 'ctr', name: 'Click-through Rate', group: 'Performance' },
          { id: 'goal_completion', name: 'Goal Completion', group: 'Performance' },
        ];
      case 'content':
        return [
          ...baseMetrics,
          { id: 'content_count', name: 'Content Count', group: 'Content' },
          { id: 'avg_time_on_page', name: 'Avg. Time on Page', group: 'Content' },
          { id: 'bounce_rate', name: 'Bounce Rate', group: 'Content' },
          { id: 'publish_count', name: 'Publish Count', group: 'Content' },
          { id: 'draft_count', name: 'Draft Count', group: 'Content' },
        ];
      case 'social':
        return [
          ...baseMetrics,
          { id: 'followers', name: 'Followers', group: 'Audience' },
          { id: 'follower_growth', name: 'Follower Growth', group: 'Audience' },
          { id: 'mentions', name: 'Mentions', group: 'Brand' },
          { id: 'sentiment', name: 'Sentiment', group: 'Brand' },
          { id: 'post_count', name: 'Post Count', group: 'Content' },
        ];
      case 'clients':
        return [
          ...baseMetrics,
          { id: 'projects_count', name: 'Projects Count', group: 'Client' },
          { id: 'active_projects', name: 'Active Projects', group: 'Client' },
          { id: 'budget_utilization', name: 'Budget Utilization', group: 'Financial' },
          { id: 'invoice_amount', name: 'Invoice Amount', group: 'Financial' },
          { id: 'payment_status', name: 'Payment Status', group: 'Financial' },
        ];
      case 'brands':
        return [
          ...baseMetrics,
          { id: 'brand_mentions', name: 'Brand Mentions', group: 'Brand' },
          { id: 'brand_sentiment', name: 'Brand Sentiment', group: 'Brand' },
          { id: 'share_of_voice', name: 'Share of Voice', group: 'Brand' },
          { id: 'competitor_mentions', name: 'Competitor Mentions', group: 'Competition' },
          { id: 'website_visits', name: 'Website Visits', group: 'Traffic' },
        ];
      default:
        return baseMetrics;
    }
  };
  
  // Available dimensions based on data source
  const getAvailableDimensions = () => {
    const baseDimensions = [
      { id: 'date', name: 'Date', group: 'Time' },
      { id: 'week', name: 'Week', group: 'Time' },
      { id: 'month', name: 'Month', group: 'Time' },
      { id: 'year', name: 'Year', group: 'Time' },
      { id: 'platform', name: 'Platform', group: 'Channel' },
    ];
    
    // Add data source specific dimensions
    switch (reportConfig.dataSource) {
      case 'campaigns':
        return [
          ...baseDimensions,
          { id: 'campaign', name: 'Campaign', group: 'Campaign' },
          { id: 'campaign_type', name: 'Campaign Type', group: 'Campaign' },
          { id: 'campaign_status', name: 'Campaign Status', group: 'Campaign' },
          { id: 'audience', name: 'Audience', group: 'Campaign' },
        ];
      case 'content':
        return [
          ...baseDimensions,
          { id: 'content_type', name: 'Content Type', group: 'Content' },
          { id: 'content_status', name: 'Content Status', group: 'Content' },
          { id: 'author', name: 'Author', group: 'Content' },
          { id: 'category', name: 'Category', group: 'Content' },
          { id: 'tags', name: 'Tags', group: 'Content' },
        ];
      case 'social':
        return [
          ...baseDimensions,
          { id: 'post_type', name: 'Post Type', group: 'Content' },
          { id: 'hashtag', name: 'Hashtag', group: 'Content' },
          { id: 'profile', name: 'Profile', group: 'Channel' },
          { id: 'audience_age', name: 'Audience Age', group: 'Audience' },
          { id: 'audience_gender', name: 'Audience Gender', group: 'Audience' },
          { id: 'audience_location', name: 'Audience Location', group: 'Audience' },
        ];
      case 'clients':
        return [
          ...baseDimensions,
          { id: 'client', name: 'Client', group: 'Client' },
          { id: 'client_industry', name: 'Client Industry', group: 'Client' },
          { id: 'project', name: 'Project', group: 'Project' },
          { id: 'project_type', name: 'Project Type', group: 'Project' },
          { id: 'service', name: 'Service', group: 'Service' },
        ];
      case 'brands':
        return [
          ...baseDimensions,
          { id: 'brand', name: 'Brand', group: 'Brand' },
          { id: 'competitor', name: 'Competitor', group: 'Competition' },
          { id: 'channel', name: 'Channel', group: 'Channel' },
          { id: 'region', name: 'Region', group: 'Geography' },
          { id: 'mention_source', name: 'Mention Source', group: 'Source' },
        ];
      default:
        return baseDimensions;
    }
  };
  
  // Available visualizations
  const visualizationTypes = [
    { id: 'table', name: 'Table', icon: <TableChartIcon /> },
    { id: 'bar', name: 'Bar Chart', icon: <BarChartIcon /> },
    { id: 'line', name: 'Line Chart', icon: <LineChartIcon /> },
    { id: 'pie', name: 'Pie Chart', icon: <PieChartIcon /> },
    { id: 'timeline', name: 'Timeline', icon: <TimelineIcon /> },
  ];
  
  // Handle stepper next button
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  
  // Handle stepper back button
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  
  // Handle stepper reset button
  const handleReset = () => {
    setActiveStep(0);
    setReportConfig({
      name: '',
      description: '',
      dataSource: 'all',
      metrics: [],
      dimensions: [],
      dateRange: { 
        startDate: dateRange.startDate, 
        endDate: dateRange.endDate
      },
      filters: [],
      visualizations: [],
      schedule: null,
      recipients: [],
      format: 'dashboard',
    });
  };
  
  // Handle change in report configuration
  const handleReportConfigChange = (field, value) => {
    const newConfig = { ...reportConfig };
    
    switch (field) {
      case 'dataSource':
        newConfig.dataSource = value;
        newConfig.metrics = []; // Reset metrics when data source changes
        newConfig.dimensions = []; // Reset dimensions when data source changes
        break;
      
      case 'addMetric':
        if (!newConfig.metrics.includes(value)) {
          newConfig.metrics = [...newConfig.metrics, value];
        }
        break;
        
      case 'removeMetric':
        newConfig.metrics = newConfig.metrics.filter(m => m !== value);
        break;
        
      case 'addDimension':
        if (!newConfig.dimensions.includes(value)) {
          newConfig.dimensions = [...newConfig.dimensions, value];
        }
        break;
        
      case 'removeDimension':
        newConfig.dimensions = newConfig.dimensions.filter(d => d !== value);
        break;
        
      case 'addFilter':
        newConfig.filters = [...newConfig.filters, { field: value.field, operator: value.operator, value: value.value }];
        break;
        
      case 'removeFilter':
        newConfig.filters = newConfig.filters.filter((_, idx) => idx !== value);
        break;
        
      case 'addVisualization':
        newConfig.visualizations = [...newConfig.visualizations, { type: value.type, title: value.title, metrics: value.metrics, dimensions: value.dimensions }];
        break;
        
      case 'removeVisualization':
        newConfig.visualizations = newConfig.visualizations.filter((_, idx) => idx !== value);
        break;
        
      case 'dateRange':
        newConfig.dateRange = value;
        break;
        
      default:
        newConfig[field] = value;
    }
    
    setReportConfig(newConfig);
  };
  
  // Handle generating report preview
  const handleGeneratePreview = async () => {
    try {
      // Prepare report config for API
      const apiConfig = {
        ...reportConfig,
        dateRange: {
          startDate: reportConfig.dateRange.startDate.toISOString().split('T')[0],
          endDate: reportConfig.dateRange.endDate.toISOString().split('T')[0],
        }
      };
      
      // Fetch custom report
      await fetchCustomReport(apiConfig);
      
      // Move to preview step
      setActiveStep(4);
      
      showSuccess('Report preview generated successfully');
    } catch (error) {
      showError('Failed to generate report preview: ' + (error.message || 'Unknown error'));
    }
  };
  
  // Handle saving report
  const handleSaveReport = () => {
    showSuccess('Report saved successfully');
    // Would typically save to backend here
    navigate('/analytics');
  };
  
  // Handle exporting report
  const handleExportReport = async () => {
    try {
      // Prepare report config for API
      const apiParams = {
        ...reportConfig,
        dateRange: {
          startDate: reportConfig.dateRange.startDate.toISOString().split('T')[0],
          endDate: reportConfig.dateRange.endDate.toISOString().split('T')[0],
        },
        format: reportConfig.format,
        // Include any additional parameters needed for custom reports
        reportName: reportConfig.name,
        metrics: reportConfig.metrics,
        dimensions: reportConfig.dimensions,
      };
      
      // Export report - this function handles the download directly
      const success = await exportAnalyticsData(apiParams);
      
      if (success) {
        showSuccess(`Report exported as ${reportConfig.format.toUpperCase()} successfully`);
      } else {
        showError('Export failed. Please try again.');
      }
    } catch (error) {
      console.error('Export error:', error);
      showError('Failed to export report: ' + (error.message || 'Unknown error'));
    }
  };
  
  // Generate sample report data for preview
  const generateSampleData = () => {
    const dates = [];
    const metrics = {};
    const now = new Date();
    
    // Initialize metrics objects
    reportConfig.metrics.forEach(metric => {
      metrics[metric] = [];
    });
    
    // Generate data for each date
    for (let i = 0; i < 12; i++) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      dates.push(date.toISOString().split('T')[0]);
      
      // Generate random values for each metric
      reportConfig.metrics.forEach(metric => {
        const baseValue = Math.floor(Math.random() * 10000);
        metrics[metric].push(baseValue);
      });
    }
    
    // Reverse arrays to get chronological order
    dates.reverse();
    reportConfig.metrics.forEach(metric => {
      metrics[metric].reverse();
    });
    
    // Format data for charts
    const timeSeriesData = dates.map((date, index) => {
      const dataPoint = { date };
      reportConfig.metrics.forEach(metric => {
        dataPoint[metric] = metrics[metric][index];
      });
      return dataPoint;
    });
    
    return {
      timeSeriesData,
      totalsByMetric: reportConfig.metrics.reduce((acc, metric) => {
        acc[metric] = metrics[metric].reduce((sum, val) => sum + val, 0);
        return acc;
      }, {}),
    };
  };
  
  // Sample preview data (would come from API in real implementation)
  const previewData = generateSampleData();
  
  // Get metric name from ID
  const getMetricName = (metricId) => {
    const metric = getAvailableMetrics().find(m => m.id === metricId);
    return metric ? metric.name : metricId;
  };
  
  // Get dimension name from ID
  const getDimensionName = (dimensionId) => {
    const dimension = getAvailableDimensions().find(d => d.id === dimensionId);
    return dimension ? dimension.name : dimensionId;
  };
  
  // Step content
  const steps = [
    {
      label: 'Basic Information',
      content: (
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Report Name"
                value={reportConfig.name}
                onChange={(e) => handleReportConfigChange('name', e.target.value)}
                placeholder="e.g. Monthly Social Media Performance"
                helperText="Give your report a descriptive name"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={reportConfig.description}
                onChange={(e) => handleReportConfigChange('description', e.target.value)}
                placeholder="Describe what this report will show"
                helperText="Optional: Add details about the purpose and audience of this report"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="data-source-label">Data Source</InputLabel>
                <Select
                  labelId="data-source-label"
                  value={reportConfig.dataSource}
                  label="Data Source"
                  onChange={(e) => handleReportConfigChange('dataSource', e.target.value)}
                >
                  <MenuItem value="all">All Data</MenuItem>
                  <MenuItem value="campaigns">Campaigns</MenuItem>
                  <MenuItem value="content">Content</MenuItem>
                  <MenuItem value="social">Social Media</MenuItem>
                  <MenuItem value="clients">Clients</MenuItem>
                  <MenuItem value="brands">Brands</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <DateRangePicker
                startDate={reportConfig.dateRange.startDate}
                endDate={reportConfig.dateRange.endDate}
                onChange={(startDate, endDate) => handleReportConfigChange('dateRange', { startDate, endDate })}
                fullWidth
                sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!reportConfig.name || !reportConfig.dataSource}
              sx={{ mr: 1 }}
            >
              Continue
            </Button>
          </Box>
        </Box>
      ),
    },
    {
      label: 'Select Metrics & Dimensions',
      content: (
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Metrics
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Select the metrics you want to include in your report
              </Typography>
              
              <Paper variant="outlined" sx={{ p: 2, mb: 2, maxHeight: 300, overflow: 'auto' }}>
                <FormGroup>
                  {getAvailableMetrics().map((metric) => (
                    <FormControlLabel
                      key={metric.id}
                      control={
                        <Checkbox
                          checked={reportConfig.metrics.includes(metric.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              handleReportConfigChange('addMetric', metric.id);
                            } else {
                              handleReportConfigChange('removeMetric', metric.id);
                            }
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">{metric.name}</Typography>
                          <Chip label={metric.group} size="small" variant="outlined" />
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>
              </Paper>
              
              <Typography variant="body2" color="textSecondary">
                Selected metrics: {reportConfig.metrics.length}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Dimensions
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Select how you want to break down your data
              </Typography>
              
              <Paper variant="outlined" sx={{ p: 2, mb: 2, maxHeight: 300, overflow: 'auto' }}>
                <FormGroup>
                  {getAvailableDimensions().map((dimension) => (
                    <FormControlLabel
                      key={dimension.id}
                      control={
                        <Checkbox
                          checked={reportConfig.dimensions.includes(dimension.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              handleReportConfigChange('addDimension', dimension.id);
                            } else {
                              handleReportConfigChange('removeDimension', dimension.id);
                            }
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">{dimension.name}</Typography>
                          <Chip label={dimension.group} size="small" variant="outlined" />
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>
              </Paper>
              
              <Typography variant="body2" color="textSecondary">
                Selected dimensions: {reportConfig.dimensions.length}
              </Typography>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={handleBack}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={reportConfig.metrics.length === 0}
              sx={{ mr: 1 }}
            >
              Continue
            </Button>
          </Box>
        </Box>
      ),
    },
    {
      label: 'Visualizations',
      content: (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Add Visualizations
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Choose how you want to visualize your data
          </Typography>
          
          <Box sx={{ mt: 2, mb: 3 }}>
            <Grid container spacing={2}>
              {visualizationTypes.map((vizType) => (
                <Grid item xs={6} sm={4} md={2} key={vizType.id}>
                  <Card
                    sx={{
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      cursor: 'pointer',
                      height: '100%',
                      border: '1px solid',
                      borderColor: theme.palette.divider,
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: 1,
                      }
                    }}
                    onClick={() => {
                      // Open a dialog or panel to configure this visualization type
                      handleReportConfigChange('addVisualization', {
                        type: vizType.id,
                        title: `${vizType.name} of ${reportConfig.metrics.map(m => getMetricName(m)).join(', ')}`,
                        metrics: reportConfig.metrics,
                        dimensions: reportConfig.dimensions.slice(0, 1),
                      });
                    }}
                  >
                    <Box sx={{ p: 1 }}>
                      {vizType.icon}
                    </Box>
                    <Typography variant="body2" align="center">
                      {vizType.name}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
          
          <Divider sx={{ my: 3 }} />
          
          <Typography variant="subtitle1" gutterBottom>
            Selected Visualizations
          </Typography>
          
          {reportConfig.visualizations.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No visualizations added yet. Click on a visualization type above to add it.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {reportConfig.visualizations.map((viz, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {viz.type === 'table' && <TableChartIcon color="primary" />}
                        {viz.type === 'bar' && <BarChartIcon color="primary" />}
                        {viz.type === 'line' && <LineChartIcon color="primary" />}
                        {viz.type === 'pie' && <PieChartIcon color="primary" />}
                        {viz.type === 'timeline' && <TimelineIcon color="primary" />}
                        <Typography variant="subtitle2">
                          {viz.title}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleReportConfigChange('removeVisualization', index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Metrics: {viz.metrics.map(m => getMetricName(m)).join(', ')}
                    </Typography>
                    
                    {viz.dimensions.length > 0 && (
                      <Typography variant="body2" color="textSecondary">
                        Dimensions: {viz.dimensions.map(d => getDimensionName(d)).join(', ')}
                      </Typography>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={handleBack}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              sx={{ mr: 1 }}
            >
              Continue
            </Button>
          </Box>
        </Box>
      ),
    },
    {
      label: 'Delivery Options',
      content: (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Report Format & Delivery
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            <FormControl component="fieldset">
              <Typography variant="subtitle2" gutterBottom>
                Export Format
              </Typography>
              <RadioGroup
                value={reportConfig.format}
                onChange={(e) => handleReportConfigChange('format', e.target.value)}
              >
                <FormControlLabel value="dashboard" control={<Radio />} label="Dashboard View" />
                <FormControlLabel value="excel" control={<Radio />} label="Excel Spreadsheet" />
                <FormControlLabel value="pdf" control={<Radio />} label="PDF Document" />
              </RadioGroup>
            </FormControl>
          </Box>
          
          <Box sx={{ mt: 3 }}>
            <FormControl component="fieldset">
              <Typography variant="subtitle2" gutterBottom>
                Schedule (Optional)
              </Typography>
              <RadioGroup
                value={reportConfig.schedule || 'none'}
                onChange={(e) => handleReportConfigChange('schedule', e.target.value === 'none' ? null : e.target.value)}
              >
                <FormControlLabel value="none" control={<Radio />} label="No schedule (on-demand only)" />
                <FormControlLabel value="daily" control={<Radio />} label="Daily" />
                <FormControlLabel value="weekly" control={<Radio />} label="Weekly" />
                <FormControlLabel value="monthly" control={<Radio />} label="Monthly" />
              </RadioGroup>
            </FormControl>
          </Box>
          
          {reportConfig.schedule && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Recipients
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Add email addresses to send the report to
              </Typography>
              
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={reportConfig.recipients}
                onChange={(_, newValue) => handleReportConfigChange('recipients', newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Add email addresses"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      color="primary"
                      variant="outlined"
                    />
                  ))
                }
              />
            </Box>
          )}
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={handleBack}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleGeneratePreview}
              sx={{ mr: 1 }}
            >
              Generate Preview
            </Button>
          </Box>
        </Box>
      ),
    },
    {
      label: 'Preview & Save',
      content: (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Report Preview
          </Typography>
          
          {/* Report Header */}
          <MagicCard magical={true} level={1} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                {reportConfig.name}
              </Typography>
              
              {reportConfig.description && (
                <Typography variant="body2" color="textSecondary" paragraph>
                  {reportConfig.description}
                </Typography>
              )}
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Date Range:</strong> {reportConfig.dateRange.startDate.toLocaleDateString()} - {reportConfig.dateRange.endDate.toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Data Source:</strong> {reportConfig.dataSource === 'all' ? 'All Data' : reportConfig.dataSource.charAt(0).toUpperCase() + reportConfig.dataSource.slice(1)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </MagicCard>
          
          {/* KPI Summary */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {Object.entries(previewData.totalsByMetric).map(([metric, value]) => (
              <Grid item xs={6} sm={4} md={3} key={metric}>
                <MagicCard level={0}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="h5">
                      {formatNumber(value, 'compact')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {getMetricName(metric)}
                    </Typography>
                  </CardContent>
                </MagicCard>
              </Grid>
            ))}
          </Grid>
          
          {/* Visualizations */}
          <Grid container spacing={3}>
            {reportConfig.visualizations.map((viz, index) => (
              <Grid item xs={12} sm={viz.type === 'table' ? 12 : 6} key={index}>
                <ChartCard
                  title={viz.title}
                  subtitle={`Based on ${viz.metrics.length} metrics and ${viz.dimensions.length} dimensions`}
                  icon={
                    viz.type === 'table' ? <TableChartIcon /> :
                    viz.type === 'bar' ? <BarChartIcon /> :
                    viz.type === 'line' ? <LineChartIcon /> :
                    viz.type === 'pie' ? <PieChartIcon /> :
                    <TimelineIcon />
                  }
                  height={350}
                >
                  <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      {viz.type.charAt(0).toUpperCase() + viz.type.slice(1)} visualization would appear here
                    </Typography>
                  </Box>
                </ChartCard>
              </Grid>
            ))}
          </Grid>
          
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={handleBack}>
              Back
            </Button>
            <Box>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportReport}
                sx={{ mr: 2 }}
              >
                Export {reportConfig.format === 'excel' ? 'Excel' : reportConfig.format === 'pdf' ? 'PDF' : 'Report'}
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveReport}
              >
                Save Report
              </Button>
            </Box>
          </Box>
        </Box>
      ),
    },
  ];
  
  return (
    <Box sx={{ py: 3 }}>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          <Link 
            color="inherit" 
            sx={{ cursor: 'pointer', textDecoration: 'none' }}
            onClick={() => navigate('/analytics')}
          >
            Analytics
          </Link>
          <Typography color="text.primary">Custom Report Builder</Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Custom Report Builder
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<TemplateIcon />}
              onClick={() => {
                // This would load a template
                showSuccess('Template loaded successfully');
              }}
            >
              Load Template
            </Button>
          </Box>
        </Box>
      </Box>
      
      {/* Main content */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <MagicCard magical={activeStep === 0}>
            <CardContent>
              <Stepper activeStep={activeStep} orientation="vertical">
                {steps.map((step, index) => (
                  <Step key={index}>
                    <StepLabel>{step.label}</StepLabel>
                    <StepContent>
                      {step.content}
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
              
              {activeStep === steps.length && (
                <Box sx={{ mt: 2 }}>
                  <Typography>All steps completed. Your report is ready.</Typography>
                  <Button onClick={handleReset} sx={{ mt: 1 }}>
                    Create Another Report
                  </Button>
                </Box>
              )}
            </CardContent>
          </MagicCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomReportBuilder;